import { useRef, useEffect, useCallback } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface FPSControlsProps {
  moveSpeed?: number;
  lookSpeed?: number;
  enabled?: boolean;
}

export function FPSControls({
  moveSpeed = 150,
  lookSpeed = 0.002,
  enabled = true,
}: FPSControlsProps) {
  const { camera, gl } = useThree();
  const isLocked = useRef(false);
  const velocity = useRef(new THREE.Vector3());
  const direction = useRef(new THREE.Vector3());
  const euler = useRef(new THREE.Euler(0, 0, 0, 'YXZ'));

  // Movement state
  const moveForward = useRef(false);
  const moveBackward = useRef(false);
  const moveLeft = useRef(false);
  const moveRight = useRef(false);
  const moveUp = useRef(false);
  const moveDown = useRef(false);
  const sprint = useRef(false);

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isLocked.current || !enabled) return;

      const movementX = event.movementX || 0;
      const movementY = event.movementY || 0;

      euler.current.setFromQuaternion(camera.quaternion);
      euler.current.y -= movementX * lookSpeed;
      euler.current.x -= movementY * lookSpeed;

      // Clamp vertical look
      euler.current.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, euler.current.x));

      camera.quaternion.setFromEuler(euler.current);
    },
    [camera, lookSpeed, enabled]
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      switch (event.code) {
        case 'KeyW':
        case 'ArrowUp':
          moveForward.current = true;
          break;
        case 'KeyS':
        case 'ArrowDown':
          moveBackward.current = true;
          break;
        case 'KeyA':
        case 'ArrowLeft':
          moveLeft.current = true;
          break;
        case 'KeyD':
        case 'ArrowRight':
          moveRight.current = true;
          break;
        case 'Space':
          moveUp.current = true;
          break;
        case 'ControlLeft':
        case 'ControlRight':
          moveDown.current = true;
          break;
        case 'ShiftLeft':
        case 'ShiftRight':
          sprint.current = true;
          break;
      }
    },
    [enabled]
  );

  const onKeyUp = useCallback((event: KeyboardEvent) => {
    switch (event.code) {
      case 'KeyW':
      case 'ArrowUp':
        moveForward.current = false;
        break;
      case 'KeyS':
      case 'ArrowDown':
        moveBackward.current = false;
        break;
      case 'KeyA':
      case 'ArrowLeft':
        moveLeft.current = false;
        break;
      case 'KeyD':
      case 'ArrowRight':
        moveRight.current = false;
        break;
      case 'Space':
        moveUp.current = false;
        break;
      case 'ControlLeft':
      case 'ControlRight':
        moveDown.current = false;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        sprint.current = false;
        break;
    }
  }, []);

  const onPointerLockChange = useCallback(() => {
    isLocked.current = document.pointerLockElement === gl.domElement;
  }, [gl]);

  const onClick = useCallback(() => {
    if (enabled && !isLocked.current) {
      gl.domElement.requestPointerLock();
    }
  }, [gl, enabled]);

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    gl.domElement.addEventListener('click', onClick);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      gl.domElement.removeEventListener('click', onClick);

      if (document.pointerLockElement === gl.domElement) {
        document.exitPointerLock();
      }
    };
  }, [gl, onMouseMove, onKeyDown, onKeyUp, onPointerLockChange, onClick]);

  useFrame((_, delta) => {
    if (!enabled) return;

    const speed = sprint.current ? moveSpeed * 3 : moveSpeed;

    // Deceleration
    velocity.current.x -= velocity.current.x * 10.0 * delta;
    velocity.current.y -= velocity.current.y * 10.0 * delta;
    velocity.current.z -= velocity.current.z * 10.0 * delta;

    // Direction
    direction.current.z = Number(moveForward.current) - Number(moveBackward.current);
    direction.current.x = Number(moveRight.current) - Number(moveLeft.current);
    direction.current.y = Number(moveUp.current) - Number(moveDown.current);
    direction.current.normalize();

    // Acceleration
    if (moveForward.current || moveBackward.current) {
      velocity.current.z -= direction.current.z * speed * delta;
    }
    if (moveLeft.current || moveRight.current) {
      velocity.current.x -= direction.current.x * speed * delta;
    }
    if (moveUp.current || moveDown.current) {
      velocity.current.y += direction.current.y * speed * delta;
    }

    // Apply movement relative to camera direction
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);

    // Remove y component for horizontal movement
    forward.y = 0;
    forward.normalize();
    right.y = 0;
    right.normalize();

    camera.position.addScaledVector(forward, -velocity.current.z * delta);
    camera.position.addScaledVector(right, -velocity.current.x * delta);
    camera.position.y += velocity.current.y * delta;
  });

  return null;
}

export default FPSControls;
